import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link, Navigate } from 'react-router-dom';
import { MERCH_TILES_PATH, PEOPLE_TILES_PATH, useDashboardStyle } from '../../context/DashboardStyleContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import StoreAdminDashboard from './StoreAdminDashboard';
import SalesRepDashboard from './SalesRepDashboard';
import SalesTeamDashboard from './SalesTeamDashboard';
import CommercialDashboard from '../commercial/CommercialDashboard';
import UserDashboardView from './UserDashboardView';
import DashboardBoard from './board/DashboardBoard';
import MaintenanceDashboard from './MaintenanceDashboard';
import TechnicianDashboard from './TechnicianDashboard';
import ThreadDashboardBoard from './thread/ThreadDashboardBoard';
import SalesLensBoard from './thread/SalesLensBoard';
import StoreLensBoard from './thread/StoreLensBoard';
import CommercialLensBoard from './thread/CommercialLensBoard';
import SalesRepLensBoard from './thread/SalesRepLensBoard';
import TechnicianLensBoard from './thread/TechnicianLensBoard';
import PeopleLensBoard from './thread/PeopleLensBoard';
import PersonalLensBoard from './thread/PersonalLensBoard';
import MaintenanceLensBoard from './thread/MaintenanceLensBoard';
import { useDashboardAccess } from './useDashboardAccess';
import { isThreadEnabled, useDashboardHome } from '../../utils/dashboardHome';

const DashboardLoading: React.FC = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress aria-label="Loading dashboard" /></Box>
);

/** A merchandiser's Tiles: the Merchandising Dashboard page when its route gate and module allow, else the personal view. */
const MerchTilesHome: React.FC = () => {
    const canMerchReports = usePermission(PERMISSIONS.MERCH_REPORT_VIEW);
    const { ready, enabledModules } = useDashboardAccess();
    if (!canMerchReports) return <UserDashboardView />;
    if (!ready) return <DashboardLoading />;
    return (enabledModules as Set<string>).has('merchandising') ? <Navigate to={MERCH_TILES_PATH} replace /> : <UserDashboardView />;
};

/** An HR manager's Tiles: the People dashboard page when its board is reachable, else the personal view. */
const PeopleTilesHome: React.FC = () => {
    const { ready, visibleModules } = useDashboardAccess();
    if (!ready) return <DashboardLoading />;
    return (visibleModules as Set<string>).has('people') ? <Navigate to={PEOPLE_TILES_PATH} replace /> : <UserDashboardView />;
};

const Dashboard: React.FC = () => {
    const dashboardStyle = useDashboardStyle();
    // ONE rule decides which home is drawn (utils/dashboardHome); DashboardStyleContext derives its switch from the same rule.
    const home = useDashboardHome();
    // Only for homes that have a Thread lens: wait for the saved choice, then draw the style. Tiles stays the default for
    // board and sales; merch defaults to Thread. Nothing is drawn until the saved choice is known.
    const canQuote = usePermission(PERMISSIONS.QUOTATION_VIEW);
    const canOrder = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
    const sellsToo = canQuote || canOrder;
    const threadHome = isThreadEnabled(home) && dashboardStyle.group === home;

    switch (home) {
        case 'systemAdmin':
            // SystemAdmin's own landing view is a platform-wide concept unrelated to any
            // institution's permission set, so it keeps reading the JWT role directly.
            return (
                <Box sx={{ p: 2 }} data-help-page="dashboard.systemAdmin">
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Welcome, System Administrator
                    </Typography>
                    <Link data-help="dashboard.systemAdmin.institutions" to="/institutions" style={{ textDecoration: 'none' }}>
                        <Typography color="primary">Manage Institutions</Typography>
                    </Link>
                </Box>
            );

        case 'board':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <ThreadDashboardBoard group="board" />;
            }
            return <DashboardBoard />;

        case 'store':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <StoreLensBoard />;
            }
            return <StoreAdminDashboard />;

        case 'commercial':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <CommercialLensBoard />;
            }
            return <CommercialDashboard />;

        case 'sales': // S2
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <SalesLensBoard />;
            }
            return <SalesTeamDashboard />;

        case 'salesRep':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <SalesRepLensBoard />;
            }
            return <SalesRepDashboard />;

        case 'maintenance':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <MaintenanceLensBoard />;
            }
            return <MaintenanceDashboard />;

        case 'technician':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <TechnicianLensBoard />;
            }
            return <TechnicianDashboard />;

        case 'merch':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <ThreadDashboardBoard group="merch" />;
            }
            // A merchandiser who also sells keeps the sales rep home they already have; the others get the Merchandising Dashboard page.
            return sellsToo ? <SalesRepDashboard /> : <MerchTilesHome />;

        case 'people':
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <PeopleLensBoard />;
            }
            return <PeopleTilesHome />;

        default:
            if (threadHome) {
                if (!dashboardStyle.ready) return <DashboardLoading />;
                if (dashboardStyle.style === 'THREAD') return <PersonalLensBoard />;
            }
            return <UserDashboardView />;
    }
};

export default Dashboard;
